import { supabase } from '../../lib/supabase';
import { JournalEntry } from './types';
import { startOfDay, endOfDay } from 'date-fns';

class JournalService {
  private sanitizeEntry(entry: Partial<JournalEntry>) {
    return {
      content: entry.content?.trim() || '',
      mood: entry.mood?.trim() || null,
      weather: entry.weather?.trim() || null,
      music: entry.music?.trim() || null,
      user_id: entry.user_id,
      created_at: entry.created_at
    };
  }

  async getEntries(userId: string): Promise<JournalEntry[]> {
    const { data, error } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async searchEntries(userId: string, query: string): Promise<JournalEntry[]> {
    const { data, error } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('user_id', userId)
      .or(`content.ilike.%${query}%, mood.ilike.%${query}%, weather.ilike.%${query}%, music.ilike.%${query}%`)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async getEntryByDate(userId: string, date: Date): Promise<JournalEntry | null> {
    const start = startOfDay(date);
    const end = endOfDay(date);

    const { data, error } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('user_id', userId)
      .gte('created_at', start.toISOString())
      .lte('created_at', end.toISOString())
      .maybeSingle();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  async createEntry(entry: Omit<JournalEntry, 'id' | 'updated_at'>): Promise<JournalEntry> {
    const sanitizedEntry = this.sanitizeEntry(entry);

    if (!sanitizedEntry.content) {
      throw new Error('O conteúdo é obrigatório');
    }

    const { data, error } = await supabase
      .from('journal_entries')
      .insert([sanitizedEntry])
      .select()
      .single();

    if (error) {
      console.error('Error creating journal entry:', error);
      throw new Error('Erro ao criar entrada do diário');
    }

    return data;
  }

  async updateEntry(id: string, entry: Partial<JournalEntry>): Promise<JournalEntry> {
    const sanitizedEntry = this.sanitizeEntry(entry);

    if (sanitizedEntry.content === '') {
      throw new Error('O conteúdo não pode ficar vazio');
    }

    const { data, error } = await supabase
      .from('journal_entries')
      .update(sanitizedEntry)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating journal entry:', error);
      throw new Error('Erro ao atualizar entrada do diário');
    }

    return data;
  }
}

export const journalService = new JournalService();
